import { processError, logError } from '../errors/errorHandler'
import axios from 'axios'

// Base URL da API (deve vir do .env)
const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8000/api'

// Timeout para requisições (30 segundos para pagamentos)
const REQUEST_TIMEOUT = 30000

// Criar instância do axios
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
})

/**
 * Buscar configuração pública de pagamento do evento
 * @returns {Promise<Object>} Configuração de pagamento (métodos, taxas, responsabilidade)
 */
export const getPaymentConfig = async () => {
  try {
    const response = await api.get('/payment-config')
    return response.data?.data || null
  } catch (error) {
    console.error('Error fetching payment config:', error)
    return null
  }
}

/**
 * Processar pagamento via cartão de crédito
 * @param {Object} paymentData - Dados do pagamento
 * @returns {Promise<Object>} Resposta do pagamento
 */
export const processCardPayment = async (paymentData) => {
  try {
    console.log('📡 Sending card payment request:', paymentData)

    const response = await api.post('/paymentCard', {
      participantId: paymentData.participantId,
      ticketId: paymentData.ticketId,
      quantity: paymentData.quantity,
      amount: paymentData.amount,
      installments: paymentData.installments || 1,
      couponId: paymentData.couponId || null,
      card: {
        token: paymentData.card.token
      },
      billingAddress: {
        number: paymentData.billingAddress?.number || '',
        address: paymentData.billingAddress?.address || '',
        neighborhood: paymentData.billingAddress?.neighborhood || '',
        complement: paymentData.billingAddress?.complement || '',
        zipCode: paymentData.billingAddress?.zipCode || '',
        city: paymentData.billingAddress?.city || '',
        state: paymentData.billingAddress?.state || ''
      },
      customer: {
        name: paymentData.customer.name,
        email: paymentData.customer.email,
        document: paymentData.customer.document,
        phone: paymentData.customer.phone
      }
    })

    console.log('📦 Card payment response:', response.data)
    return response.data
  } catch (error) {
    console.error('❌ Card payment error:', error)
    const processedError = processError(error, {
      operation: 'processCardPayment',
      amount: paymentData.amount,
      installments: paymentData.installments
    })
    logError(processedError)
    throw processedError
  }
}

/**
 * Gerar pagamento PIX
 * @param {Object} pixData - Dados para gerar PIX
 * @returns {Promise<Object>} Dados do PIX (QR Code, código copia e cola)
 */
export const generatePixPayment = async (pixData) => {
  try {
    console.log('📡 Sending PIX payment request:', pixData)

    const response = await api.post('/paymentPix', {
      participantId: pixData.participantId,
      ticketId: pixData.ticketId,
      couponId: pixData.couponId || null,
      name: pixData.customer.name,
      email: pixData.customer.email,
      document: pixData.customer.document,
      phone: pixData.customer.phone,
      amount: pixData.amount,
      description: pixData.description,
      itemCode: pixData.ticketId
    })

    console.log('📦 PIX payment response:', response.data)

    // O backend retorna no formato Pagar.me
    const pixResponse = {
      qr_code: response.data.charges?.[0]?.last_transaction?.qr_code || '',
      qr_code_url: response.data.charges?.[0]?.last_transaction?.qr_code_url || '',
      expires_at: response.data.charges?.[0]?.last_transaction?.expires_at || '',
      payment_id: response.data.charges?.[0]?.id || '',
      order_id: response.data.id || ''
    }

    console.log('✅ Formatted PIX response:', pixResponse)
    return pixResponse
  } catch (error) {
    console.error('❌ PIX payment error:', error)
    const processedError = processError(error, {
      operation: 'generatePixPayment',
      participantId: pixData.participantId
    })
    logError(processedError)
    throw processedError
  }
}

/**
 * Finalizar pedido gratuito (cupom 100% de desconto).
 * Pula gateway de pagamento, cria ParticipantOrder com status=paid direto.
 * Backend: POST /paymentFree.
 *
 * @param {Object} freeData - { participantId, ticketId, couponId }
 * @returns {Promise<Object>} { order_id, status: 'paid' }
 */
export const processFreePayment = async (freeData) => {
  try {
    const response = await api.post('/paymentFree', {
      participantId: freeData.participantId,
      ticketId: freeData.ticketId,
      couponId: freeData.couponId || null
    })
    return response.data
  } catch (error) {
    const processedError = processError(error, {
      operation: 'processFreePayment',
      participantId: freeData.participantId
    })
    logError(processedError)
    throw processedError
  }
}

/**
 * Gerar boleto bancário
 * @param {Object} boletoData - Dados para gerar boleto
 * @returns {Promise<Object>} Dados do boleto (PDF, código de barras)
 */
export const generateBoletoPayment = async (boletoData) => {
  try {
    console.log('📡 Sending Boleto payment request:', boletoData)

    const response = await api.post('/paymentBoleto', {
      participantId: boletoData.participantId,
      ticketId: boletoData.ticketId,
      couponId: boletoData.couponId || null,
      name: boletoData.customer.name,
      email: boletoData.customer.email,
      document: boletoData.customer.document,
      phone: boletoData.customer.phone,
      address: boletoData.customer.address,
      number: boletoData.customer.number,
      zipcode: boletoData.customer.zipcode,
      city: boletoData.customer.city,
      state: boletoData.customer.state,
      amount: boletoData.amount,
      description: boletoData.description,
      itemCode: boletoData.ticketId
    })

    console.log('📦 Boleto payment response:', response.data)

    // O backend retorna no formato Pagar.me
    const boletoResponse = {
      boleto_url: response.data.charges?.[0]?.last_transaction?.pdf || response.data.boleto_url || '',
      boleto_barcode: response.data.charges?.[0]?.last_transaction?.line || response.data.boleto_barcode || '',
      due_at: response.data.charges?.[0]?.last_transaction?.due_at || '',
      payment_id: response.data.charges?.[0]?.id || '',
      order_id: response.data.id || ''
    }

    console.log('✅ Formatted Boleto response:', boletoResponse)
    return boletoResponse
  } catch (error) {
    console.error('❌ Boleto payment error:', error)
    const processedError = processError(error, {
      operation: 'generateBoletoPayment',
      participantId: boletoData.participantId
    })
    logError(processedError)
    throw processedError
  }
}

/**
 * Consultar status de um pagamento PIX via charge_id
 * @param {string} chargeId - ID da cobrança no Pagar.me (charge_id)
 * @returns {Promise<Object>} Status do pagamento
 */
export const getPaymentStatus = async (chargeId) => {
  try {
    const response = await api.get(`/checkPix/${chargeId}`)

    return {
      status: response.data.status || 'pending',
      paid: response.data.status === 'paid',
      amount: response.data.paid_amount || 0,
      order_id: response.data.order_id || '',
      charge_id: response.data.charge_id || '',
      participant_id: response.data.participant_id || ''
    }
  } catch (error) {
    const processedError = processError(error, {
      operation: 'getPaymentStatus',
      chargeId
    })
    logError(processedError)
    throw processedError
  }
}

/**
 * Calcular parcelas disponíveis para um valor
 * @param {number} amount - Valor total
 * @param {number} maxInstallments - Máximo de parcelas permitido
 * @param {number} minInstallmentValue - Valor mínimo da parcela
 * @returns {Array} Lista de opções de parcelas
 */
export const calculateInstallments = (amount, maxInstallments = 12, minInstallmentValue = 5) => {
  const installments = []
  const totalAmount = parseFloat(amount)

  if (isNaN(totalAmount) || totalAmount <= 0) {
    return installments
  }

  for (let i = 1; i <= maxInstallments; i++) {
    const installmentValue = totalAmount / i

    // Não permitir parcelas menores que o valor mínimo
    if (installmentValue < minInstallmentValue && i > 1) {
      break
    }

    installments.push({
      number: i,
      value: installmentValue,
      total: totalAmount,
      label: i === 1
        ? `À vista - ${formatCurrency(totalAmount)}`
        : `${i}x de ${formatCurrency(installmentValue)} (Total: ${formatCurrency(totalAmount)})`
    })
  }

  return installments
}

/**
 * Formatar valor em reais
 * @param {number} value - Valor a formatar
 * @returns {string} Valor formatado
 */
export const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value)
}

/**
 * Monitorar status do pagamento PIX (polling)
 * @param {string} chargeId - ID da cobrança (charge_id do Pagar.me)
 * @param {Function} onStatusChange - Callback quando status mudar
 * @param {number} interval - Intervalo de verificação em ms (padrão: 5000)
 * @param {number} maxAttempts - Máximo de tentativas (padrão: 120 = 10 minutos)
 * @returns {Object} Controle do polling { stop: Function }
 */
export const monitorPixPayment = (chargeId, onStatusChange, interval = 5000, maxAttempts = 120) => {
  let attempts = 0
  let stopped = false

  const checkStatus = async () => {
    if (stopped || attempts >= maxAttempts) {
      return
    }

    attempts++

    try {
      const status = await getPaymentStatus(chargeId)

      if (onStatusChange) {
        onStatusChange(status)
      }

      // Se pagamento foi aprovado ou recusado, para o polling
      if (status.status === 'paid' || status.status === 'failed' || status.status === 'canceled') {
        stopped = true
        return
      }

      // Continua verificando
      if (!stopped && attempts < maxAttempts) {
        setTimeout(checkStatus, interval)
      }
    } catch (error) {
      logError(error)

      // Em caso de erro, continua tentando
      if (!stopped && attempts < maxAttempts) {
        setTimeout(checkStatus, interval)
      }
    }
  }

  // Inicia o polling
  setTimeout(checkStatus, interval)

  // Retorna função para parar o polling
  return {
    stop: () => {
      stopped = true
    }
  }
}

/**
 * Validar dados do cartão de crédito
 * @param {Object} cardData - Dados do cartão
 * @returns {Object} Resultado da validação
 */
export const validateCardData = (cardData) => {
  const errors = []

  if (!cardData.number || cardData.number.replace(/\s/g, '').length < 13) {
    errors.push('Número do cartão inválido')
  }

  if (!cardData.holderName || cardData.holderName.length < 3) {
    errors.push('Nome do titular inválido')
  }

  if (!cardData.expMonth || cardData.expMonth < 1 || cardData.expMonth > 12) {
    errors.push('Mês de validade inválido')
  }

  if (!cardData.expYear || cardData.expYear.length < 2) {
    errors.push('Ano de validade inválido')
  }

  // Verificar se cartão está vencido
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1
  const expYear = cardData.expYear.length === 2
    ? parseInt('20' + cardData.expYear)
    : parseInt(cardData.expYear)

  if (expYear < currentYear || (expYear === currentYear && parseInt(cardData.expMonth) < currentMonth)) {
    errors.push('Cartão vencido')
  }

  if (!cardData.cvv || cardData.cvv.length < 3) {
    errors.push('CVV inválido')
  }

  if (!cardData.holderDocument || cardData.holderDocument.replace(/\D/g, '').length < 11) {
    errors.push('CPF do titular inválido')
  }

  return {
    valid: errors.length === 0,
    errors
  }
}
